/// <amd-module name="Feed/Legacy/Components/Settings/Permissions" />
import * as React from "react";
import { connect } from "react-redux";

import { CheckboxVisibility, ColumnActionsMode, ConstrainMode, DetailsListLayoutMode, IColumn } from "OfficeFabric/DetailsList";
import { IObjectWithKey, Selection, SelectionMode } from "OfficeFabric/Selection";

import { IVssPageContext } from "VSS/Platform/Context";

import { Icon } from "VSSUI/Icon";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { TooltipHost } from "VSSUI/Tooltip";
import { VssDetailsList } from "VSSUI/VssDetailsList";

import { SettingsPivotKeys } from "Feed/Common/Constants/Constants";
import { FeedPermission } from "Feed/Common/Contracts/Feed";
import * as PackageResources from "Feed/Common/Resources";
import { IFeedHubViewAwareState, isSelectedFeedDeleted } from "Feed/Core/Redux/FeedHub";
import { FeedSettingsActionCreator } from "Feed/Legacy/Actions/FeedSettingsActionCreator";
import { NoResultsPane } from "Feed/Legacy/Components/NoResultsPane";
import { AddUsersOrGroupsPanel } from "Feed/Legacy/Components/Settings/AddUsersOrGroupsPanel";
import { RoleHelper } from "Feed/Legacy/Helpers/RoleHelper";

export interface IPermissionsProps {
    pageContext: IVssPageContext;
    /**
     * Show loading spinner while getting permission for selected feed
     */
    isLoading: boolean;

    /**
     * Keeps track of whether data was fetched from server when this component was mounted
     */
    isDataLoadedFromServer: boolean;

    /**
     * List of permissions set on feed
     */
    permissions: FeedPermission[];

    /**
     * There are no permissions
     */
    hasNoPermissions: boolean;

    /**
     * No permissions after filtering
     */
    hasNoFilterResults: boolean;

    /**
     * Enable/disable project collection-scoped build and release permission button
     */
    collectionScopedPermissionIsDisplayed: boolean;

    /**
     * Enable/disable project-scoped build and release permission button
     */
    projectScopedPermissionIsDisplayed: boolean;

    /**
     * True if <Project Name> Build Service (<account/collection name>) identity exists
     */
    projectScopedPermissionExists: boolean;

    /**
     * If True, display add users or groups Panel to user
     */
    showAddUsersOrGroupsPanel: boolean;

    /**
     * Bag that contains validation errors
     */
    validationErrorBag: { [key: string]: string };

    /**
     * True if the current user can make changes to the feed settings
     */
    isUserAdmin: boolean;

    /**
     * Identity Id of project collection administrator group
     */
    projectCollectionAdminGroupId: string;

    /**
     * Tracks if permission is getting saved to server
     */
    isSavingChanges: boolean;

    /**
     * While saving, captures errors if they occur to show in UI
     */
    error: Error;
}

export interface IPermissionsProps2 {
    /**
     * tracks if feed is deleted
     */
    isDeletedFeed: boolean;
}

class UnconnectedPermissions extends React.PureComponent<IPermissionsProps & IPermissionsProps2> {
    constructor(props: IPermissionsProps & IPermissionsProps2) {
        super(props);

        this.selection = new Selection({
            canSelectItem: (item: IObjectWithKey): boolean => {
                if (this.props.isUserAdmin === false || this.props.isDeletedFeed) {
                    return false;
                }

                const permission: FeedPermission = item as FeedPermission;
                if (permission.identityId === this.props.projectCollectionAdminGroupId) {
                    return false;
                }

                if (permission.isInheritedRole) {
                    return false;
                }

                return true;
            },
            getKey: (item: IObjectWithKey): string => {
                const permission: FeedPermission = item as FeedPermission;
                return permission.identityId;
            },
            onSelectionChanged: (): void => {
                const permissions = this.selection.getSelection() as FeedPermission[];
                FeedSettingsActionCreator.onPermissionSelectionChanged.invoke(permissions);
            }
        });
    }

    public componentWillMount(): void {
        if (this.props.isDataLoadedFromServer === false) {
            FeedSettingsActionCreator.navigatingToPermissions.invoke({});
        }
    }

    public render(): JSX.Element {
        return (
            <div className="permissions-content">
                {this.props.isLoading ? (
                    <Spinner className="loading-spinner" size={SpinnerSize.medium} />
                ) : (
                        this._getMessageOrList()
                    )}
                <AddUsersOrGroupsPanel
                    pageContext={this.props.pageContext}
                    isOpen={this.props.showAddUsersOrGroupsPanel}
                    validationErrorBag={this.props.validationErrorBag}
                    isSavingChanges={this.props.isSavingChanges}
                    error={this.props.error}
                />
            </div>
        );
    }

    private _getMessageOrList(): JSX.Element {
        if (this.props.hasNoPermissions) {
            return (
                <NoResultsPane
                    pageContext={this.props.pageContext}
                    header={PackageResources.FeedSettings_Permissions_NoPermissions}
                    subheader={PackageResources.FeedSettings_Permissions_Description}
                    iconClass={"bowtie-cloud-fill"}
                />
            );
        }

        if (this.props.hasNoFilterResults) {
            return (
                <NoResultsPane
                    pageContext={this.props.pageContext}
                    header={PackageResources.FeedSettings_Permissions_NoPermissionsMatch}
                    iconClass={"bowtie-search"}
                />
            );
        }

        return (
            <VssDetailsList
                className="permissions-grid"
                setKey="permissions-grid"
                constrainMode={ConstrainMode.unconstrained}
                layoutMode={DetailsListLayoutMode.justified}
                items={this.props.permissions}
                allocateSpaceForActionsButtonWhileHidden={false}
                columns={this._getColumns()}
                selectionMode={SelectionMode.multiple}
                selectionPreservedOnEmptyClick={true}
                selection={this.selection}
                checkboxVisibility={CheckboxVisibility.onHover}
                ariaLabel={PackageResources.FeedSettings_Permissions_Grid_AriaLabel}
                ariaLabelForGrid={PackageResources.FeedSettings_Permissions_Grid_AriaLabelForGrid}
                ariaLabelForListHeader={PackageResources.FeedSettings_Permissions_Grid_AriaLabelForHeader}
                ariaLabelForSelectionColumn={PackageResources.DetailsList_SelectionColumn_AriaLabel}
                ariaLabelForSelectAllCheckbox={PackageResources.DetailsList_SelectAll_AriaLabel}
                checkButtonAriaLabel={PackageResources.DetailsList_Select_AriaLabel}
            />
        );
    }

    private _getColumns(): IColumn[] {
        return [
            {
                key: "entity",
                fieldName: "entity",
                name: PackageResources.FeedSettings_Permissions_Grid_UserGroup,
                columnActionsMode: ColumnActionsMode.disabled,
                onRender: (permission: FeedPermission): JSX.Element => {
                    return <label className="settings-grid-cell">{permission.displayName}</label>;
                },
                minWidth: 300,
                maxWidth: 400,
                isResizable: true
            },
            {
                key: "role",
                fieldName: "role",
                name: PackageResources.FeedSettings_Permissions_Grid_Role,
                columnActionsMode: ColumnActionsMode.disabled,
                onRender: (permission: FeedPermission): JSX.Element => {
                    return (
                        <label className="settings-grid-cell">{RoleHelper.roleToLocaleString(permission.role)}</label>
                    );
                },
                minWidth: 200,
                maxWidth: 200
            },
            {
                key: "inheritance",
                fieldName: "inheritance",
                name: PackageResources.FeedSettings_Permissions_Grid_Inheritance,
                columnActionsMode: ColumnActionsMode.disabled,
                onRender: (permission: FeedPermission): JSX.Element => {
                    return permission.isInheritedRole ? (
                        <TooltipHost
                            id={"feed-permission-tooltip"}
                            content={PackageResources.FeedSettings_Permissions_Grid_Inheritance_ToolTip}
                        >
                            <Icon
                                ariaLabel={PackageResources.FeedSettings_Permissions_Grid_Inheritance_AriaLabel}
                                ariaDescribedBy={"feed-permission-tooltip"}
                                iconName="CheckMark"
                            />
                        </TooltipHost>
                    ) : null;
                },
                minWidth: 60,
                maxWidth: 60
            }
        ];
    }

    protected getPivotKey(): string {
        return SettingsPivotKeys.permissions;
    }

    private selection: Selection;
}

function mapStateToProps(state: IFeedHubViewAwareState): IPermissionsProps2 {
    return {
        isDeletedFeed: isSelectedFeedDeleted(state)
    };
}

export const Permissions = connect(mapStateToProps)(UnconnectedPermissions);
